<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import { store } from '@/store/appStore.js'
const kpiFilter = ref('All')
const orders = computed(() => store.data.orders.value)
const kpis = computed(() => {
  const o = orders.value
  const delivered = o.filter(x => x.currentStage === 'Delivered' && !x.rejected).length
  const cancelled = o.filter(x => x.rejected).length
  // BRD Module 6 defines Yellow as "Minor delay / SLA nearing breach" — it IS
  // a delay. This KPI previously counted only Orange+Red, which meant Order
  // Analytics showed "Delayed: 0" while the Delayed Orders Report showed 3
  // rows for the same scenario. Aligned to the BRD definition and to Reports.
  const delayed = o.filter(x => ['Yellow','Orange','Red'].includes(x.trafficLight) && !x.rejected).length
  // Pending excludes cancellations — a rejected order isn't "awaiting
  // fulfilment", and counting it in both buckets double-reports it.
  const pending = o.filter(x => x.currentStage !== 'Delivered' && !x.rejected).length
  // BRD defines Order Fulfilment % as Delivered / Total Submitted. Cancelled
  // orders are excluded from the denominator: they were withdrawn, not failed
  // to fulfil, so leaving them in would understate operational performance.
  const fulfillable = o.length - cancelled
  return {
    total: o.length, delivered, pending, delayed, cancelled,
    fulfillmentPct: fulfillable ? Math.round(delivered / fulfillable * 100) : 0
  }
})
const filteredOrders = computed(() => {
  if (kpiFilter.value==='All') return orders.value
  if (kpiFilter.value==='Delivered') return orders.value.filter(o=>o.currentStage==='Delivered' && !o.rejected)
  if (kpiFilter.value==='Pending')   return orders.value.filter(o=>o.currentStage!=='Delivered' && !o.rejected)
  if (kpiFilter.value==='Delayed')   return orders.value.filter(o=>['Yellow','Orange','Red'].includes(o.trafficLight) && !o.rejected)
  if (kpiFilter.value==='Cancelled') return orders.value.filter(o=>o.rejected)
  return orders.value
})
const STAGES = ['Order Received','Telecaller Validation','Stock Allocation','Warehouse Processing','Dispatch Pending','Dispatched','Invoice Generated','Delivered']
const stageChart = computed(() => ({ labels:STAGES, datasets:[{ label:'Orders', data:STAGES.map(s=>orders.value.filter(o=>o.currentStage===s).length), backgroundColor:'#0F3D3E' }] }))
const terrChart = computed(() => {
  const byT={}, pm=Object.fromEntries(store.data.pharmacies.value.map(p=>[p.customerId,p.territory]))
  orders.value.forEach(o=>{const t=pm[o.customerId]||'Unknown'; byT[t]=(byT[t]||0)+1})
  const labels=Object.keys(byT).sort()
  return { labels, datasets:[{ label:'Orders', data:labels.map(l=>byT[l]), backgroundColor:'#C99A2E' }] }
})

// BRD Module 13 lists Heat Maps alongside Trend/Pie/Territory charts. Built
// as a plain CSS grid rather than a Chart.js plugin — Chart.js has no native
// matrix chart type, and adding one would mean a new dependency outside the
// pinned set. Cell intensity is row-normalized (% of THAT territory's own
// orders), not raw count: Harare naturally has ~5x Bulawayo's order volume,
// so a raw-count heat map would just re-display volume, not relative health.
// A territory with 1 Red order out of 6 total is materially worse off than
// one with 1 Red out of 33, even though the raw counts match.
const STATUS_COLUMNS = ['Green', 'Yellow', 'Orange', 'Red', 'Cancelled']
const STATUS_COLOR = { Green: '58,155,92', Yellow: '201,154,46', Orange: '217,117,46', Red: '193,57,43', Cancelled: '107,112,117' }
const heatMap = computed(() => {
  const pm = Object.fromEntries(store.data.pharmacies.value.map(p=>[p.customerId,p.territory]))
  const byTerr = {}
  orders.value.forEach(o => {
    const t = pm[o.customerId] || 'Unknown'
    const status = o.rejected ? 'Cancelled' : o.trafficLight
    byTerr[t] = byTerr[t] || { territory: t, total: 0, counts: {} }
    byTerr[t].total += 1
    byTerr[t].counts[status] = (byTerr[t].counts[status] || 0) + 1
  })
  return Object.values(byTerr)
    .map(row => ({
      ...row,
      cells: STATUS_COLUMNS.map(status => {
        const count = row.counts[status] || 0
        const pct = row.total ? count / row.total : 0
        return { status, count, pct }
      })
    }))
    .sort((a,b) => b.total - a.total)
})
function heatCellStyle(cell) {
  if (cell.count === 0) return 'background:transparent;color:var(--color-text-muted);'
  // Floor of 0.15 so even a low-rate cell with a real order is visible;
  // scales up to full color at 100% of that row's orders.
  const alpha = Math.min(1, Math.max(0.15, cell.pct))
  return `background:rgba(${STATUS_COLOR[cell.status]},${alpha});color:${cell.pct > 0.45 ? '#fff' : 'var(--color-text)'};font-weight:600;`
}

// Cancellation Reason Breakdown — real data from actual rejected orders
// (Order Tracking's Reject action, Phase 9 categorized). Empty by default
// since rejection is a user-triggered action in this demo, not baseline
// scenario data — an honest empty state beats fabricating cancellations
// that never happened.
const cancelledOrders = computed(() => orders.value.filter(o => o.rejected))
const cancelChart = computed(() => {
  const byReason = {}
  cancelledOrders.value.forEach(o => { const r = o.rejectReason || 'Other'; byReason[r] = (byReason[r]||0) + 1 })
  const labels = Object.keys(byReason)
  return { labels, datasets: [{ data: labels.map(l=>byReason[l]), backgroundColor: ['#C1392B','#D9752E','#C99A2E','#5B4E9E','#3A9B5C','#6B7075'] }] }
})
</script>
<template>
  <div>
    <div class="card module-header"><h2>Order fulfillment overview</h2></div>
    <div class="stat-row">
      <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="kpiFilter=kpiFilter==='All'?'All':'All'"><div class="stat-value">{{ kpis.total }}</div><div class="stat-label">Total Orders</div></button>
      <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="kpiFilter=kpiFilter==='Delivered'?'All':'Delivered'"><div class="stat-value" style="color:var(--status-green);">{{ kpis.delivered }}</div><div class="stat-label">Delivered</div></button>
      <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="kpiFilter=kpiFilter==='Pending'?'All':'Pending'"><div class="stat-value" style="color:var(--color-accent);">{{ kpis.pending }}</div><div class="stat-label">Pending</div></button>
      <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="kpiFilter=kpiFilter==='Delayed'?'All':'Delayed'"><div class="stat-value" style="color:var(--status-red);">{{ kpis.delayed }}</div><div class="stat-label">Delayed</div></button>
      <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="kpiFilter=kpiFilter==='Cancelled'?'All':'Cancelled'"><div class="stat-value" style="color:var(--color-text-muted);">{{ kpis.cancelled }}</div><div class="stat-label">Cancelled</div></button>
      <div class="card stat-card"><div class="stat-value">{{ kpis.fulfillmentPct }}%</div><div class="stat-label" title="Delivered ÷ (Total − Cancelled)">Fulfillment %</div></div>
    </div>
    <div class="split-main-side" style="margin-bottom:16px;">
      <div class="card" style="padding:18px 20px;"><div class="eyebrow" style="margin-bottom:8px;">Orders by Stage</div><ChartView type="bar" :labels="stageChart.labels" :datasets="stageChart.datasets" :height="260" key="stage-bar" /></div>
      <div class="card" style="padding:18px 20px;"><div class="eyebrow" style="margin-bottom:8px;">Orders by Territory</div><ChartView type="bar" :labels="terrChart.labels" :datasets="terrChart.datasets" :height="260" key="terr-bar" /></div>
    </div>
    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Territory × Status Heat Map</div>
      <p class="muted" style="font-size:11.5px;margin:0 0 12px;">Shading is row-normalized — % of each territory's own orders, not raw count, so territories with different order volumes are comparable on relative health.</p>
      <div class="table-wrap">
        <table class="data-table" style="text-align:center;">
          <thead>
            <tr><th style="text-align:left;">Territory</th><th>Total</th><th v-for="s in STATUS_COLUMNS" :key="s">{{ s }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in heatMap" :key="row.territory">
              <td class="cell-strong" style="text-align:left;">{{ row.territory }}</td>
              <td class="cell-muted">{{ row.total }}</td>
              <td v-for="cell in row.cells" :key="cell.status" :style="heatCellStyle(cell)">
                {{ cell.count || '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Cancellation Reason Breakdown</div>
      <div v-if="!cancelledOrders.length" class="muted" style="font-size:13px;padding:20px 0;">
        No orders have been rejected this session. Reject an order from <strong>Order Tracking</strong> (Telecaller, Warehouse, or Admin) to see this breakdown populate.
      </div>
      <ChartView v-else type="doughnut" :labels="cancelChart.labels" :datasets="cancelChart.datasets" :height="240" :key="'cancel-'+cancelledOrders.length" />
    </div>
    <div class="card">
      <div class="toolbar"><span class="result-count">{{ kpiFilter==='All'?'All orders':'Filtered to '+kpiFilter }} — {{ filteredOrders.length }} shown</span><button v-if="kpiFilter!=='All'" class="filter-select" style="cursor:pointer;" @click="kpiFilter='All'">Clear filter</button></div>
      <div class="table-wrap"><table class="data-table"><thead><tr><th>Order No.</th><th>Pharmacy</th><th>Product</th><th>Stage</th><th>Status</th></tr></thead>
        <tbody><tr v-for="o in filteredOrders" :key="o.orderNo"><td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.pharmacyName }}</td><td>{{ o.productName }}</td><td class="cell-muted">{{ o.currentStage }}</td><td><StatusPill :status="o.trafficLight" /></td></tr></tbody>
      </table></div>
    </div>
  </div>
</template>